import { Constants } from "./constants.js";
import { Box2D } from "box2d-deterministic";

// These really only need to be called once, but we do it here
Box2D.Common.b2Settings.b2_maxTranslation = 8.0;//4.0;
Box2D.Common.b2Settings.b2_maxTranslationSquared = 64.0;//16.0;
Box2D.Common.b2Settings.b2_velocityThreshold = 0.0;

export class B2DUtils {
    static _manifoldInstance = null;

    static createMaze(b2dWorld, maze, wallWidth) {
        // Setup helper array which is used to determine if colliders have already been created for a given maze tile.
        const tiles = maze.getTiles();

        const tempArray = Array.from({ length: tiles.length }, () => Array.from({ length: tiles[0].length }, () => ({ topWallColliderCreated: false, leftWallColliderCreated: false })));

        // Create collision bodies.
        for (let i = 0; i < tiles.length; ++i) {
            for (let j = 0; j < tiles[0].length; ++j) {
                // Top wall.
                if (tiles[i][j][1] === 1 && !tempArray[i][j].topWallColliderCreated) {
                    // The points containing the collider geometry (must be convex).
                    const points = [];

                    // First, check for how the starting end of the collider should be.
                    if (j - 1 < 0 || tiles[i][j - 1][2] === 1) {
                        points[points.length] = (i * Constants.MAZE_TILE_SIZE.m) - (wallWidth / 2.0) + wallWidth;
                        points[points.length] = (j * Constants.MAZE_TILE_SIZE.m) - (wallWidth / 2.0);
                    } else {
                        points[points.length] = (i * Constants.MAZE_TILE_SIZE.m) - (wallWidth / 2.0);
                        points[points.length] = (j * Constants.MAZE_TILE_SIZE.m) - (wallWidth / 2.0);
                    }
                    if (tiles[i][j][2] === 1) {
                        points[points.length] = (i * Constants.MAZE_TILE_SIZE.m) - (wallWidth / 2.0) + wallWidth;
                        points[points.length] = (j * Constants.MAZE_TILE_SIZE.m) + (wallWidth / 2.0);
                    } else {
                        points[points.length] = (i * Constants.MAZE_TILE_SIZE.m) - (wallWidth / 2.0);
                        points[points.length] = (j * Constants.MAZE_TILE_SIZE.m) + (wallWidth / 2.0);
                    }

                    // Then, step along to the right until finding the other end of the collider.
                    let iStep = i;
                    while (iStep < tiles.length) {
                        if (tiles[iStep][j][1] === 1) {
                            tempArray[iStep][j].topWallColliderCreated = true;
                            ++iStep;
                        } else {
                            break;
                        }
                    }

                    // Finally, check for how the ending end of the collider should be.
                    if (iStep === tiles.length || tiles[iStep][j][2] === 1) {
                        points[points.length] = (iStep * Constants.MAZE_TILE_SIZE.m) + (wallWidth / 2.0) - wallWidth;
                        points[points.length] = (j * Constants.MAZE_TILE_SIZE.m) + (wallWidth / 2.0);
                    } else {
                        points[points.length] = (iStep * Constants.MAZE_TILE_SIZE.m) + (wallWidth / 2.0);
                        points[points.length] = (j * Constants.MAZE_TILE_SIZE.m) + (wallWidth / 2.0);
                    }
                    if (iStep === tiles.length || j - 1 < 0 || tiles[iStep][j - 1][2] === 1) {
                        points[points.length] = (iStep * Constants.MAZE_TILE_SIZE.m) + (wallWidth / 2.0) - wallWidth;
                        points[points.length] = (j * Constants.MAZE_TILE_SIZE.m) - (wallWidth / 2.0);
                    } else {
                        points[points.length] = (iStep * Constants.MAZE_TILE_SIZE.m) + (wallWidth / 2.0);
                        points[points.length] = (j * Constants.MAZE_TILE_SIZE.m) - (wallWidth / 2.0);
                    }

                    const wallVertices = [
                        Box2D.Common.Math.b2Vec2.Make(points[6], points[7]),
                        Box2D.Common.Math.b2Vec2.Make(points[4], points[5]),
                        Box2D.Common.Math.b2Vec2.Make(points[2], points[3]),
                        Box2D.Common.Math.b2Vec2.Make(points[0], points[1])
                    ];

                    this._createWallBody(b2dWorld, wallVertices, maze);
                }

                // Left wall.
                if (tiles[i][j][2] === 1 && !tempArray[i][j].leftWallColliderCreated) {
                    // The points containing the collider geometry (must be convex).
                    const points = [];

                    // First, check for how the starting end of the collider should be.
                    if (i - 1 < 0 || tiles[i - 1][j][1] === 1) {
                        points[points.length] = (i * Constants.MAZE_TILE_SIZE.m) - (wallWidth / 2.0);
                        points[points.length] = (j * Constants.MAZE_TILE_SIZE.m) - (wallWidth / 2.0) + wallWidth;
                    } else {
                        points[points.length] = (i * Constants.MAZE_TILE_SIZE.m) - (wallWidth / 2.0);
                        points[points.length] = (j * Constants.MAZE_TILE_SIZE.m) - (wallWidth / 2.0);
                    }
                    if (tiles[i][j][1] === 1) {
                        points[points.length] = (i * Constants.MAZE_TILE_SIZE.m) + (wallWidth / 2.0);
                        points[points.length] = (j * Constants.MAZE_TILE_SIZE.m) - (wallWidth / 2.0) + wallWidth;
                    } else {
                        points[points.length] = (i * Constants.MAZE_TILE_SIZE.m) + (wallWidth / 2.0);
                        points[points.length] = (j * Constants.MAZE_TILE_SIZE.m) - (wallWidth / 2.0);
                    }

                    // Then, step along down until finding the other end of the collider.
                    let jStep = j;
                    while (jStep < tiles[0].length) {
                        if (tiles[i][jStep][2] === 1) {
                            tempArray[i][jStep].leftWallColliderCreated = true;
                            ++jStep;
                        } else {
                            break;
                        }
                    }

                    // Finally, check for how the ending end of the collider should be.
                    if (jStep === tiles[0].length || tiles[i][jStep][1] === 1) {
                        points[points.length] = (i * Constants.MAZE_TILE_SIZE.m) + (wallWidth / 2.0);
                        points[points.length] = (jStep * Constants.MAZE_TILE_SIZE.m) + (wallWidth / 2.0) - wallWidth;
                    } else {
                        points[points.length] = (i * Constants.MAZE_TILE_SIZE.m) + (wallWidth / 2.0);
                        points[points.length] = (jStep * Constants.MAZE_TILE_SIZE.m) + (wallWidth / 2.0);
                    }
                    if (jStep === tiles[0].length || i - 1 < 0 || tiles[i - 1][jStep][1] === 1) {
                        points[points.length] = (i * Constants.MAZE_TILE_SIZE.m) - (wallWidth / 2.0);
                        points[points.length] = (jStep * Constants.MAZE_TILE_SIZE.m) + (wallWidth / 2.0) - wallWidth;
                    } else {
                        points[points.length] = (i * Constants.MAZE_TILE_SIZE.m) - (wallWidth / 2.0);
                        points[points.length] = (jStep * Constants.MAZE_TILE_SIZE.m) + (wallWidth / 2.0);
                    }

                    const wallVertices = [
                        Box2D.Common.Math.b2Vec2.Make(points[0], points[1]),
                        Box2D.Common.Math.b2Vec2.Make(points[2], points[3]),
                        Box2D.Common.Math.b2Vec2.Make(points[4], points[5]),
                        Box2D.Common.Math.b2Vec2.Make(points[6], points[7])
                    ];

                    this._createWallBody(b2dWorld, wallVertices, maze);
                }
            }
        }
        for (let i = 0; i < tiles.length; ++i) {
            // Bottom wall.
            if (tiles[i][tiles[0].length - 1][0] === 1) {
                // The points containing the collider geometry (must be convex).
                const points = [];

                points[points.length] = (i * Constants.MAZE_TILE_SIZE.m) - (wallWidth / 2.0);
                points[points.length] = (tiles[0].length * Constants.MAZE_TILE_SIZE.m) - (wallWidth / 2.0);
                points[points.length] = (i * Constants.MAZE_TILE_SIZE.m) - (wallWidth / 2.0);
                points[points.length] = (tiles[0].length * Constants.MAZE_TILE_SIZE.m) + (wallWidth / 2.0);

                // Step along to the right until finding the other end of the collider.
                while (i < tiles.length) {
                    if (tiles[i][tiles[0].length - 1][0] === 1) {
                        ++i;
                    } else {
                        break;
                    }
                }

                points[points.length] = (i * Constants.MAZE_TILE_SIZE.m) + (wallWidth / 2.0);
                points[points.length] = (tiles[0].length * Constants.MAZE_TILE_SIZE.m) + (wallWidth / 2.0);
                points[points.length] = (i * Constants.MAZE_TILE_SIZE.m) + (wallWidth / 2.0);
                points[points.length] = (tiles[0].length * Constants.MAZE_TILE_SIZE.m) - (wallWidth / 2.0);

                const wallVertices = [
                    Box2D.Common.Math.b2Vec2.Make(points[6], points[7]),
                    Box2D.Common.Math.b2Vec2.Make(points[4], points[5]),
                    Box2D.Common.Math.b2Vec2.Make(points[2], points[3]),
                    Box2D.Common.Math.b2Vec2.Make(points[0], points[1])
                ];

                this._createWallBody(b2dWorld, wallVertices, maze);
            }
        }
        for (let j = 0; j < tiles[0].length; ++j) {
            // Right wall.
            if (tiles.at(-1)[j][0] === 1) {
                // The points containing the collider geometry (must be convex).
                const points = [];

                points[points.length] = (tiles.length * Constants.MAZE_TILE_SIZE.m) - (wallWidth / 2.0);
                points[points.length] = (j * Constants.MAZE_TILE_SIZE.m) - (wallWidth / 2.0);
                points[points.length] = (tiles.length * Constants.MAZE_TILE_SIZE.m) + (wallWidth / 2.0);
                points[points.length] = (j * Constants.MAZE_TILE_SIZE.m) - (wallWidth / 2.0);

                // Step along down until finding the other end of the collider.
                while (j < tiles[0].length) {
                    if (tiles.at(-1)[j][0] === 1) {
                        ++j;
                    } else {
                        break;
                    }
                }

                points[points.length] = (tiles.length * Constants.MAZE_TILE_SIZE.m) + (wallWidth / 2.0);
                points[points.length] = (j * Constants.MAZE_TILE_SIZE.m) + (wallWidth / 2.0);
                points[points.length] = (tiles.length * Constants.MAZE_TILE_SIZE.m) - (wallWidth / 2.0);
                points[points.length] = (j * Constants.MAZE_TILE_SIZE.m) + (wallWidth / 2.0);

                const wallVertices = [
                    Box2D.Common.Math.b2Vec2.Make(points[0], points[1]),
                    Box2D.Common.Math.b2Vec2.Make(points[2], points[3]),
                    Box2D.Common.Math.b2Vec2.Make(points[4], points[5]),
                    Box2D.Common.Math.b2Vec2.Make(points[6], points[7])
                ];

                this._createWallBody(b2dWorld, wallVertices, maze);
            }
        }
    }

    static _createWallBody(b2dWorld, wallVertices, maze) {
        const wallFixtureDef = new Box2D.Dynamics.b2FixtureDef();
        wallFixtureDef.density = 0.0;
        wallFixtureDef.friction = 0.05;
        wallFixtureDef.restitution = 0.0;

        wallFixtureDef.shape = Box2D.Collision.Shapes.b2PolygonShape.AsArray(wallVertices);
        wallFixtureDef.userData = { gameObject: maze };
        wallFixtureDef.filter = new Box2D.Dynamics.b2FilterData();
        wallFixtureDef.filter.categoryBits = Constants.COLLISION_CATEGORIES.MAZE;
        wallFixtureDef.filter.maskBits = Constants.COLLISION_CATEGORIES.TANK |
            Constants.COLLISION_CATEGORIES.PROJECTILE |
            Constants.COLLISION_CATEGORIES.TRAP;

        const wallBodyDef = new Box2D.Dynamics.b2BodyDef();
        wallBodyDef.type = Box2D.Dynamics.b2Body.b2_staticBody;

        const box2dBody = b2dWorld.CreateBody(wallBodyDef);
        box2dBody.CreateFixture(wallFixtureDef);

        return box2dBody;
    }

    static createProjectileBody(b2dWorld, projectile, radius) {
        // Enable physics on the projectile.
        const projectileFixtureDef = new Box2D.Dynamics.b2FixtureDef();
        projectileFixtureDef.density = 0.01;
        projectileFixtureDef.friction = 0.0;
        projectileFixtureDef.restitution = 1.0;
        projectileFixtureDef.shape = new Box2D.Collision.Shapes.b2CircleShape(radius);
        projectileFixtureDef.userData = { gameObject: projectile };
        projectileFixtureDef.filter = new Box2D.Dynamics.b2FilterData();
        projectileFixtureDef.filter.categoryBits = Constants.COLLISION_CATEGORIES.PROJECTILE;
        projectileFixtureDef.filter.maskBits =
            Constants.COLLISION_CATEGORIES.TANK |
            Constants.COLLISION_CATEGORIES.MAZE |
            Constants.COLLISION_CATEGORIES.SHIELD |
            Constants.COLLISION_CATEGORIES.ZONE;

        const projectileBodyDef = new Box2D.Dynamics.b2BodyDef();
        projectileBodyDef.type = Box2D.Dynamics.b2Body.b2_dynamicBody;
        projectileBodyDef.linearDamping = 0.0;
        projectileBodyDef.fixedRotation = true;
        projectileBodyDef.active = true;
        // Prevents tunneling through dynamic bodies such as the tanks!
        projectileBodyDef.bullet = true;
        const box2dBody = b2dWorld.CreateBody(projectileBodyDef);
        box2dBody.CreateFixture(projectileFixtureDef);
        box2dBody.SetPosition(
            Box2D.Common.Math.b2Vec2.Make(projectile.getX(), projectile.getY())
        );
        box2dBody.SetLinearVelocity(
            Box2D.Common.Math.b2Vec2.Make(projectile.getSpeedX(), projectile.getSpeedY())
        );

        return box2dBody;
    }

    static createTrapBody(b2dWorld, trap, radius) {
        // Enable physics on the trap.
        const trapFixtureDef = new Box2D.Dynamics.b2FixtureDef();
        trapFixtureDef.density = 0.1;
        trapFixtureDef.friction = 0.0;
        trapFixtureDef.restitution = 1.0;
        trapFixtureDef.shape = new Box2D.Collision.Shapes.b2CircleShape(radius);
        trapFixtureDef.userData = { gameObject: trap };
        trapFixtureDef.filter = new Box2D.Dynamics.b2FilterData();
        trapFixtureDef.filter.categoryBits = Constants.COLLISION_CATEGORIES.TRAP;
        trapFixtureDef.filter.maskBits =
            Constants.COLLISION_CATEGORIES.TRAP |
            Constants.COLLISION_CATEGORIES.TANK |
            Constants.COLLISION_CATEGORIES.MAZE |
            Constants.COLLISION_CATEGORIES.ZONE;

        const trapBodyDef = new Box2D.Dynamics.b2BodyDef();
        trapBodyDef.type = Box2D.Dynamics.b2Body.b2_dynamicBody;
        trapBodyDef.linearDamping = 0.0;
        trapBodyDef.fixedRotation = true;
        trapBodyDef.active = true;
        trapBodyDef.allowSleep = false;
        const box2dBody = b2dWorld.CreateBody(trapBodyDef);
        box2dBody.CreateFixture(trapFixtureDef);
        box2dBody.SetPosition(
            Box2D.Common.Math.b2Vec2.Make(trap.getX(), trap.getY())
        );
        box2dBody.SetLinearVelocity(
            Box2D.Common.Math.b2Vec2.Make(trap.getSpeedX(), trap.getSpeedY())
        );

        return box2dBody;
    }

    static createCrateBody(b2dWorld, collectible) {
        //enable physics on the crate
        const crateFixtureDef = new Box2D.Dynamics.b2FixtureDef();
        crateFixtureDef.density = 0.1;
        crateFixtureDef.friction = 0.0;
        crateFixtureDef.restitution = 1.0;
        crateFixtureDef.shape = Box2D.Collision.Shapes.b2PolygonShape.AsBox(
            Constants.CRATE.WIDTH.m / 2.0,
            Constants.CRATE.HEIGHT.m / 2.0
        );
        crateFixtureDef.userData = { gameObject: collectible };
        crateFixtureDef.filter = new Box2D.Dynamics.b2FilterData();
        crateFixtureDef.filter.categoryBits = Constants.COLLISION_CATEGORIES.COLLECTIBLE;
        crateFixtureDef.filter.maskBits =
            Constants.COLLISION_CATEGORIES.TANK;

        const crateBodyDef = new Box2D.Dynamics.b2BodyDef();
        crateBodyDef.type = Box2D.Dynamics.b2Body.b2_dynamicBody;
        crateBodyDef.angle = collectible.getRotation();
        crateBodyDef.linearDamping = 4.0;
        crateBodyDef.angularDamping = 4.0;
        crateBodyDef.fixedRotation = false;
        crateBodyDef.active = true;
        crateBodyDef.allowSleep = false;
        const box2dBody = b2dWorld.CreateBody(crateBodyDef);
        box2dBody.CreateFixture(crateFixtureDef);
        box2dBody.SetPosition(
            Box2D.Common.Math.b2Vec2.Make(collectible.getX(), collectible.getY())
        );

        return box2dBody;
    }

    static createGoldBody(b2dWorld, collectible) {
        //enable physics on the gold
        const goldFixtureDef = new Box2D.Dynamics.b2FixtureDef();
        goldFixtureDef.density = 0.1;
        goldFixtureDef.friction = 0.0;
        goldFixtureDef.restitution = 1.0;
        goldFixtureDef.shape = new Box2D.Collision.Shapes.b2CircleShape(Constants.GOLD.RADIUS.m);
        goldFixtureDef.userData = { gameObject: collectible };
        goldFixtureDef.filter = new Box2D.Dynamics.b2FilterData();
        goldFixtureDef.filter.categoryBits = Constants.COLLISION_CATEGORIES.COLLECTIBLE;
        goldFixtureDef.filter.maskBits =
            Constants.COLLISION_CATEGORIES.TANK;

        const goldBodyDef = new Box2D.Dynamics.b2BodyDef();
        goldBodyDef.type = Box2D.Dynamics.b2Body.b2_dynamicBody;
        goldBodyDef.linearDamping = 4.0;
        goldBodyDef.fixedRotation = true;
        goldBodyDef.active = true;
        goldBodyDef.allowSleep = false;
        const box2dBody = b2dWorld.CreateBody(goldBodyDef);
        box2dBody.CreateFixture(goldFixtureDef);
        box2dBody.SetPosition(
            Box2D.Common.Math.b2Vec2.Make(collectible.getX(), collectible.getY())
        );

        return box2dBody;
    }

    static createDiamondBody(b2dWorld, collectible) {
        //enable physics on the diamond
        const diamondFixtureDef = new Box2D.Dynamics.b2FixtureDef();
        diamondFixtureDef.density = 0.1;
        diamondFixtureDef.friction = 0.0;
        diamondFixtureDef.restitution = 1.0;
        const diamondVertices = [
            Box2D.Common.Math.b2Vec2.Make(0, Constants.DIAMOND.HEIGHT.m / 2.0),
            Box2D.Common.Math.b2Vec2.Make(-Constants.DIAMOND.WIDTH.m / 2.0, Constants.DIAMOND.MIDDLE_HEIGHT.m / 2.0),
            Box2D.Common.Math.b2Vec2.Make(-Constants.DIAMOND.WIDTH.m / 2.0, -Constants.DIAMOND.MIDDLE_HEIGHT.m / 2.0),
            Box2D.Common.Math.b2Vec2.Make(0, -Constants.DIAMOND.HEIGHT.m / 2.0),
            Box2D.Common.Math.b2Vec2.Make(Constants.DIAMOND.WIDTH.m / 2.0, -Constants.DIAMOND.MIDDLE_HEIGHT.m / 2.0),
            Box2D.Common.Math.b2Vec2.Make(Constants.DIAMOND.WIDTH.m / 2.0, Constants.DIAMOND.MIDDLE_HEIGHT.m / 2.0)
        ];

        diamondFixtureDef.shape = Box2D.Collision.Shapes.b2PolygonShape.AsArray(diamondVertices);
        diamondFixtureDef.userData = { gameObject: collectible };
        diamondFixtureDef.filter = new Box2D.Dynamics.b2FilterData();
        diamondFixtureDef.filter.categoryBits = Constants.COLLISION_CATEGORIES.COLLECTIBLE;
        diamondFixtureDef.filter.maskBits =
            Constants.COLLISION_CATEGORIES.TANK;

        const diamondBodyDef = new Box2D.Dynamics.b2BodyDef();
        diamondBodyDef.type = Box2D.Dynamics.b2Body.b2_dynamicBody;
        diamondBodyDef.angle = collectible.getRotation();
        diamondBodyDef.linearDamping = 4.0;
        diamondBodyDef.fixedRotation = true;
        diamondBodyDef.active = true;
        diamondBodyDef.allowSleep = false;
        const box2dBody = b2dWorld.CreateBody(diamondBodyDef);
        box2dBody.CreateFixture(diamondFixtureDef);
        box2dBody.SetPosition(
            Box2D.Common.Math.b2Vec2.Make(collectible.getX(), collectible.getY())
        );

        return box2dBody;
    }

    static createSpawnZoneBody(b2dWorld, zone, radius) {
        const spawnZoneFixtureDef = new Box2D.Dynamics.b2FixtureDef();
        spawnZoneFixtureDef.density = 0.0;
        spawnZoneFixtureDef.friction = 0.0;
        spawnZoneFixtureDef.restitution = 1.0;

        spawnZoneFixtureDef.shape = new Box2D.Collision.Shapes.b2CircleShape(radius);
        spawnZoneFixtureDef.userData = { gameObject: zone };
        spawnZoneFixtureDef.filter = new Box2D.Dynamics.b2FilterData();
        spawnZoneFixtureDef.filter.categoryBits = Constants.COLLISION_CATEGORIES.ZONE;
        spawnZoneFixtureDef.filter.maskBits = Constants.COLLISION_CATEGORIES.TANK |
            Constants.COLLISION_CATEGORIES.PROJECTILE |
            Constants.COLLISION_CATEGORIES.TRAP |
            Constants.COLLISION_CATEGORIES.SHIELD;

        const spawnZoneBodyDef = new Box2D.Dynamics.b2BodyDef();
        spawnZoneBodyDef.type = Box2D.Dynamics.b2Body.b2_staticBody;

        const box2dBody = b2dWorld.CreateBody(spawnZoneBodyDef);
        box2dBody.CreateFixture(spawnZoneFixtureDef);
        const [position] = zone.getTiles();
        box2dBody.SetPosition(
            Box2D.Common.Math.b2Vec2.Make((position.x + 0.5) * Constants.MAZE_TILE_SIZE.m, (position.y + 0.5) * Constants.MAZE_TILE_SIZE.m)
        );

        return box2dBody;
    }

    static updateSpawnZoneBody(b2body, zone, radius) {
        const fixture = b2body.GetFixtureList();
        b2body.DestroyFixture(fixture);

        const spawnZoneFixtureDef = new Box2D.Dynamics.b2FixtureDef();
        spawnZoneFixtureDef.density = 0.0;
        spawnZoneFixtureDef.friction = 0.0;
        spawnZoneFixtureDef.restitution = 1.0;

        spawnZoneFixtureDef.shape = new Box2D.Collision.Shapes.b2CircleShape(radius);
        spawnZoneFixtureDef.userData = { gameObject: zone };
        spawnZoneFixtureDef.filter = new Box2D.Dynamics.b2FilterData();
        spawnZoneFixtureDef.filter.categoryBits = Constants.COLLISION_CATEGORIES.ZONE;
        spawnZoneFixtureDef.filter.maskBits = Constants.COLLISION_CATEGORIES.TANK |
            Constants.COLLISION_CATEGORIES.PROJECTILE |
            Constants.COLLISION_CATEGORIES.TRAP |
            Constants.COLLISION_CATEGORIES.SHIELD;

        b2body.CreateFixture(spawnZoneFixtureDef);
    }

    static createStormZoneBody(b2dWorld, zone, stormStartRight, stormEndRight, stormStartBottom, stormEndBottom, stormStartLeft, stormEndLeft, stormStartTop, stormEndTop) {
        const stormZoneRightFixtureDef = B2DUtils._createStormZoneFixtureDef(zone, stormStartRight, stormStartTop, stormEndRight, stormEndBottom);
        const stormZoneBottomFixtureDef = B2DUtils._createStormZoneFixtureDef(zone, stormStartLeft, stormStartBottom, stormEndRight, stormEndBottom);
        const stormZoneLeftFixtureDef = B2DUtils._createStormZoneFixtureDef(zone, stormStartLeft, stormStartTop, stormEndLeft, stormEndBottom);
        const stormZoneTopFixtureDef = B2DUtils._createStormZoneFixtureDef(zone, stormStartLeft, stormStartTop, stormEndRight, stormEndTop);

        const stormZoneBodyDef = new Box2D.Dynamics.b2BodyDef();
        stormZoneBodyDef.type = Box2D.Dynamics.b2Body.b2_staticBody;

        const box2dBody = b2dWorld.CreateBody(stormZoneBodyDef);
        box2dBody.CreateFixture(stormZoneRightFixtureDef);
        box2dBody.CreateFixture(stormZoneBottomFixtureDef);
        box2dBody.CreateFixture(stormZoneLeftFixtureDef);
        box2dBody.CreateFixture(stormZoneTopFixtureDef);
        box2dBody.SetPosition(
            Box2D.Common.Math.b2Vec2.Make(0.0, 0.0)
        );

        return box2dBody;
    }

    static updateStormZoneBody(b2body, zone, stormStartRight, stormEndRight, stormStartBottom, stormEndBottom, stormStartLeft, stormEndLeft, stormStartTop, stormEndTop) {
        let currentFixture = b2body.GetFixtureList();
        while (currentFixture !== null) {
            const nextFixture = currentFixture.GetNext();
            b2body.DestroyFixture(currentFixture);
            currentFixture = nextFixture;
        }

        const stormZoneRightFixtureDef = B2DUtils._createStormZoneFixtureDef(zone, stormStartRight, stormStartTop, stormEndRight, stormEndBottom);
        const stormZoneBottomFixtureDef = B2DUtils._createStormZoneFixtureDef(zone, stormStartLeft, stormStartBottom, stormEndRight, stormEndBottom);
        const stormZoneLeftFixtureDef = B2DUtils._createStormZoneFixtureDef(zone, stormStartLeft, stormStartTop, stormEndLeft, stormEndBottom);
        const stormZoneTopFixtureDef = B2DUtils._createStormZoneFixtureDef(zone, stormStartLeft, stormStartTop, stormEndRight, stormEndTop);

        b2body.CreateFixture(stormZoneRightFixtureDef);
        b2body.CreateFixture(stormZoneBottomFixtureDef);
        b2body.CreateFixture(stormZoneLeftFixtureDef);
        b2body.CreateFixture(stormZoneTopFixtureDef);
    }

    static _createStormZoneFixtureDef(zone, topLeftX, topLeftY, bottomRightX, bottomRightY) {
        const stormZoneFixtureDef = new Box2D.Dynamics.b2FixtureDef();
        stormZoneFixtureDef.density = 0.0;
        stormZoneFixtureDef.friction = 0.0;
        stormZoneFixtureDef.restitution = 1.0;

        const vertices = [
            Box2D.Common.Math.b2Vec2.Make(topLeftX, topLeftY),
            Box2D.Common.Math.b2Vec2.Make(bottomRightX, topLeftY),
            Box2D.Common.Math.b2Vec2.Make(bottomRightX, bottomRightY),
            Box2D.Common.Math.b2Vec2.Make(topLeftX, bottomRightY)
        ];

        stormZoneFixtureDef.shape = Box2D.Collision.Shapes.b2PolygonShape.AsArray(vertices);
        stormZoneFixtureDef.userData = { gameObject: zone };
        stormZoneFixtureDef.filter = new Box2D.Dynamics.b2FilterData();
        stormZoneFixtureDef.filter.categoryBits = Constants.COLLISION_CATEGORIES.ZONE;
        stormZoneFixtureDef.filter.maskBits = Constants.COLLISION_CATEGORIES.TANK;

        return stormZoneFixtureDef;
    }

    static addTankBodyUpgrade(b2body, upgrade) {
        // Remove any old upgrade of type.
        B2DUtils.removeTankBodyUpgrade(b2body, upgrade.getType());

        // Add new upgrade.
        let newFixtureDefs = [];
        switch (upgrade.getType()) {
            case Constants.UPGRADE_TYPES.LASER_AIMER:
            case Constants.UPGRADE_TYPES.AIMER:
            case Constants.UPGRADE_TYPES.SPEED_BOOST:
            {
                // Intentionally create no new fixtures.
                break;
            }
            case Constants.UPGRADE_TYPES.SPAWN_SHIELD:
            case Constants.UPGRADE_TYPES.SHIELD:
            {
                newFixtureDefs = B2DUtils._createShieldFixtureDefs(upgrade);
                break;
            }
        }

        for (const newFixtureDef of newFixtureDefs) {
            b2body.CreateFixture(newFixtureDef);
        }
    }

    static removeTankBodyUpgrade(b2body, upgradeType) {
        let currentFixture = b2body.GetFixtureList();
        while (currentFixture !== null) {
            if (currentFixture.GetUserData().type === upgradeType) {
                const nextFixture = currentFixture.GetNext();
                b2body.DestroyFixture(currentFixture);
                currentFixture = nextFixture;
            } else {
                currentFixture = currentFixture.GetNext();
            }
        }
    }

    static _createShieldFixtureDefs(upgrade) {
        const shieldFixtureDef = new Box2D.Dynamics.b2FixtureDef();
        shieldFixtureDef.density = 10.0;
        shieldFixtureDef.friction = 0.0;
        shieldFixtureDef.restitution = 1.0;
        shieldFixtureDef.shape = new Box2D.Collision.Shapes.b2CircleShape(Constants.SHIELD.RADIUS.m);
        shieldFixtureDef.userData = { type: upgrade.getType(), gameObject: upgrade };
        shieldFixtureDef.filter = new Box2D.Dynamics.b2FilterData();
        shieldFixtureDef.filter.categoryBits = Constants.COLLISION_CATEGORIES.SHIELD;
        shieldFixtureDef.filter.maskBits =
            Constants.COLLISION_CATEGORIES.TANK |
            Constants.COLLISION_CATEGORIES.PROJECTILE |
            Constants.COLLISION_CATEGORIES.SHIELD |
            Constants.COLLISION_CATEGORIES.ZONE;

        return [shieldFixtureDef];
    }

    static updateTankBodyTurret(b2body, tank, weapon) {
        // Remove old tank turret.
        let currentFixture = b2body.GetFixtureList();
        while (currentFixture !== null) {
            if (currentFixture.GetUserData().type === "turret") {
                const nextFixture = currentFixture.GetNext();
                b2body.DestroyFixture(currentFixture);
                currentFixture = nextFixture;
            } else {
                currentFixture = currentFixture.GetNext();
            }
        }

        // Add new tank turret.
        let newFixtureDefs = [];
        switch (weapon.getType()) {
            case Constants.WEAPON_TYPES.BULLET:
            {
                newFixtureDefs = B2DUtils._createBulletTurretFixtureDefs(tank);
                break;
            }
            case Constants.WEAPON_TYPES.LASER:
            {
                newFixtureDefs = B2DUtils._createLaserTurretFixtureDefs(tank);
                break;
            }
            case Constants.WEAPON_TYPES.DOUBLE_BARREL:
            {
                newFixtureDefs = B2DUtils._createDoubleBarrelTurretFixtureDefs(tank);
                break;
            }
            case Constants.WEAPON_TYPES.SHOTGUN:
            {
                newFixtureDefs = B2DUtils._createShotgunTurretFixtureDef(tank);
                break;
            }
            case Constants.WEAPON_TYPES.HOMING_MISSILE:
            {
                if (!weapon.getField("launched")) {
                    newFixtureDefs = B2DUtils._createMissileTurretFixtureDef(tank);
                }
                break;
            }
            case Constants.WEAPON_TYPES.GATLING_GUN:
            {
                newFixtureDefs = B2DUtils._createGatlingGunTurretFixtureDefs(tank);
                break;
            }
            case Constants.WEAPON_TYPES.MINE:
            {
                // Intentionally empty.
                break;
            }
        }

        for (const newFixtureDef of newFixtureDefs) {
            b2body.CreateFixture(newFixtureDef);
        }
    }

    static _createBulletTurretFixtureDefs(tank) {
        const turretFixtureDef = new Box2D.Dynamics.b2FixtureDef();
        turretFixtureDef.density = 0.0;
        turretFixtureDef.friction = 0.25;
        turretFixtureDef.restitution = 0.0;
        const turretVertices = [
            Box2D.Common.Math.b2Vec2.Make(Constants.BULLET_TURRET.OFFSET_X.m + (Constants.BULLET_TURRET.WIDTH.m / 2.0), Constants.BULLET_TURRET.OFFSET_Y.m + (Constants.BULLET_TURRET.HEIGHT.m / 2.0)),
            Box2D.Common.Math.b2Vec2.Make(Constants.BULLET_TURRET.OFFSET_X.m - (Constants.BULLET_TURRET.WIDTH.m / 2.0), Constants.BULLET_TURRET.OFFSET_Y.m + (Constants.BULLET_TURRET.HEIGHT.m / 2.0)),
            Box2D.Common.Math.b2Vec2.Make(Constants.BULLET_TURRET.OFFSET_X.m - (Constants.BULLET_TURRET.WIDTH.m / 2.0), Constants.BULLET_TURRET.OFFSET_Y.m - (Constants.BULLET_TURRET.HEIGHT.m / 2.0)),
            Box2D.Common.Math.b2Vec2.Make(Constants.BULLET_TURRET.OFFSET_X.m + (Constants.BULLET_TURRET.WIDTH.m / 2.0), Constants.BULLET_TURRET.OFFSET_Y.m - (Constants.BULLET_TURRET.HEIGHT.m / 2.0))
        ];

        turretFixtureDef.shape = Box2D.Collision.Shapes.b2PolygonShape.AsArray(turretVertices);
        turretFixtureDef.userData = { type: "turret", gameObject: tank };
        turretFixtureDef.filter = new Box2D.Dynamics.b2FilterData();
        turretFixtureDef.filter.categoryBits = Constants.COLLISION_CATEGORIES.TANK;
        turretFixtureDef.filter.maskBits =
            Constants.COLLISION_CATEGORIES.TANK |
            Constants.COLLISION_CATEGORIES.MAZE |
            Constants.COLLISION_CATEGORIES.PROJECTILE |
            Constants.COLLISION_CATEGORIES.COLLECTIBLE |
            Constants.COLLISION_CATEGORIES.SHIELD |
            Constants.COLLISION_CATEGORIES.ZONE;

        return [turretFixtureDef];
    }

    static _createLaserTurretFixtureDefs(tank) {
        const dishFixtureDef = new Box2D.Dynamics.b2FixtureDef();
        dishFixtureDef.density = 0.0;
        dishFixtureDef.friction = 0.25;
        dishFixtureDef.restitution = 0.0;
        const dishVertices = [
            Box2D.Common.Math.b2Vec2.Make(Constants.LASER_TURRET.DISH_OFFSET_X.m + (Constants.LASER_TURRET.DISH_WIDTH.m / 2.0), Constants.LASER_TURRET.DISH_OFFSET_Y.m + (Constants.LASER_TURRET.DISH_HEIGHT.m / 2.0)),
            Box2D.Common.Math.b2Vec2.Make(Constants.LASER_TURRET.DISH_OFFSET_X.m - (Constants.LASER_TURRET.DISH_WIDTH.m / 2.0), Constants.LASER_TURRET.DISH_OFFSET_Y.m + (Constants.LASER_TURRET.DISH_HEIGHT.m / 2.0)),
            Box2D.Common.Math.b2Vec2.Make(Constants.LASER_TURRET.DISH_OFFSET_X.m - (Constants.LASER_TURRET.DISH_WIDTH.m / 2.0), Constants.LASER_TURRET.DISH_OFFSET_Y.m - (Constants.LASER_TURRET.DISH_HEIGHT.m / 2.0)),
            Box2D.Common.Math.b2Vec2.Make(Constants.LASER_TURRET.DISH_OFFSET_X.m + (Constants.LASER_TURRET.DISH_WIDTH.m / 2.0), Constants.LASER_TURRET.DISH_OFFSET_Y.m - (Constants.LASER_TURRET.DISH_HEIGHT.m / 2.0))
        ];

        dishFixtureDef.shape = Box2D.Collision.Shapes.b2PolygonShape.AsArray(dishVertices);
        dishFixtureDef.userData = { type: "turret", gameObject: tank };
        dishFixtureDef.filter = new Box2D.Dynamics.b2FilterData();
        dishFixtureDef.filter.categoryBits = Constants.COLLISION_CATEGORIES.TANK;
        dishFixtureDef.filter.maskBits =
            Constants.COLLISION_CATEGORIES.TANK |
            Constants.COLLISION_CATEGORIES.MAZE |
            Constants.COLLISION_CATEGORIES.PROJECTILE |
            Constants.COLLISION_CATEGORIES.COLLECTIBLE |
            Constants.COLLISION_CATEGORIES.SHIELD |
            Constants.COLLISION_CATEGORIES.ZONE;

        const antennaFixtureDef = new Box2D.Dynamics.b2FixtureDef();
        antennaFixtureDef.density = 0.0;
        antennaFixtureDef.friction = 0.25;
        antennaFixtureDef.restitution = 0.0;
        const antennaVertices = [
            Box2D.Common.Math.b2Vec2.Make(Constants.LASER_TURRET.ANTENNA_OFFSET_X.m + (Constants.LASER_TURRET.ANTENNA_WIDTH.m / 2.0), Constants.LASER_TURRET.ANTENNA_OFFSET_Y.m + (Constants.LASER_TURRET.ANTENNA_HEIGHT.m / 2.0)),
            Box2D.Common.Math.b2Vec2.Make(Constants.LASER_TURRET.ANTENNA_OFFSET_X.m - (Constants.LASER_TURRET.ANTENNA_WIDTH.m / 2.0), Constants.LASER_TURRET.ANTENNA_OFFSET_Y.m + (Constants.LASER_TURRET.ANTENNA_HEIGHT.m / 2.0)),
            Box2D.Common.Math.b2Vec2.Make(Constants.LASER_TURRET.ANTENNA_OFFSET_X.m - (Constants.LASER_TURRET.ANTENNA_WIDTH.m / 2.0), Constants.LASER_TURRET.ANTENNA_OFFSET_Y.m - (Constants.LASER_TURRET.ANTENNA_HEIGHT.m / 2.0)),
            Box2D.Common.Math.b2Vec2.Make(Constants.LASER_TURRET.ANTENNA_OFFSET_X.m + (Constants.LASER_TURRET.ANTENNA_WIDTH.m / 2.0), Constants.LASER_TURRET.ANTENNA_OFFSET_Y.m - (Constants.LASER_TURRET.ANTENNA_HEIGHT.m / 2.0))
        ];

        antennaFixtureDef.shape = Box2D.Collision.Shapes.b2PolygonShape.AsArray(antennaVertices);
        antennaFixtureDef.userData = { type: "turret", gameObject: tank };
        antennaFixtureDef.filter = new Box2D.Dynamics.b2FilterData();
        antennaFixtureDef.filter.categoryBits = Constants.COLLISION_CATEGORIES.TANK;
        antennaFixtureDef.filter.maskBits =
            Constants.COLLISION_CATEGORIES.TANK |
            Constants.COLLISION_CATEGORIES.MAZE |
            Constants.COLLISION_CATEGORIES.PROJECTILE |
            Constants.COLLISION_CATEGORIES.COLLECTIBLE |
            Constants.COLLISION_CATEGORIES.SHIELD |
            Constants.COLLISION_CATEGORIES.ZONE;

        return [dishFixtureDef, antennaFixtureDef];
    }

    static _createDoubleBarrelTurretFixtureDefs(tank) {
        const turretFixtureDef = new Box2D.Dynamics.b2FixtureDef();
        turretFixtureDef.density = 0.0;
        turretFixtureDef.friction = 0.25;
        turretFixtureDef.restitution = 0.0;
        const turretVertices = [
            Box2D.Common.Math.b2Vec2.Make(Constants.DOUBLE_BARREL_TURRET.OFFSET_X.m + (Constants.DOUBLE_BARREL_TURRET.WIDTH.m / 2.0), Constants.DOUBLE_BARREL_TURRET.OFFSET_Y.m + (Constants.DOUBLE_BARREL_TURRET.HEIGHT.m / 2.0)),
            Box2D.Common.Math.b2Vec2.Make(Constants.DOUBLE_BARREL_TURRET.OFFSET_X.m - (Constants.DOUBLE_BARREL_TURRET.WIDTH.m / 2.0), Constants.DOUBLE_BARREL_TURRET.OFFSET_Y.m + (Constants.DOUBLE_BARREL_TURRET.HEIGHT.m / 2.0)),
            Box2D.Common.Math.b2Vec2.Make(Constants.DOUBLE_BARREL_TURRET.OFFSET_X.m - (Constants.DOUBLE_BARREL_TURRET.WIDTH.m / 2.0), Constants.DOUBLE_BARREL_TURRET.OFFSET_Y.m - (Constants.DOUBLE_BARREL_TURRET.HEIGHT.m / 2.0)),
            Box2D.Common.Math.b2Vec2.Make(Constants.DOUBLE_BARREL_TURRET.OFFSET_X.m + (Constants.DOUBLE_BARREL_TURRET.WIDTH.m / 2.0), Constants.DOUBLE_BARREL_TURRET.OFFSET_Y.m - (Constants.DOUBLE_BARREL_TURRET.HEIGHT.m / 2.0))
        ];

        turretFixtureDef.shape = Box2D.Collision.Shapes.b2PolygonShape.AsArray(turretVertices);
        turretFixtureDef.userData = { type: "turret", gameObject: tank };
        turretFixtureDef.filter = new Box2D.Dynamics.b2FilterData();
        turretFixtureDef.filter.categoryBits = Constants.COLLISION_CATEGORIES.TANK;
        turretFixtureDef.filter.maskBits =
            Constants.COLLISION_CATEGORIES.TANK |
            Constants.COLLISION_CATEGORIES.MAZE |
            Constants.COLLISION_CATEGORIES.PROJECTILE |
            Constants.COLLISION_CATEGORIES.COLLECTIBLE |
            Constants.COLLISION_CATEGORIES.SHIELD |
            Constants.COLLISION_CATEGORIES.ZONE;

        return [turretFixtureDef];
    }

    static _createShotgunTurretFixtureDef(tank) {
        const turretFixtureDef = new Box2D.Dynamics.b2FixtureDef();
        turretFixtureDef.density = 0.0;
        turretFixtureDef.friction = 0.25;
        turretFixtureDef.restitution = 0.0;
        const turretVertices = [
            Box2D.Common.Math.b2Vec2.Make(Constants.SHOTGUN_TURRET.OFFSET_X.m + (Constants.SHOTGUN_TURRET.WIDTH.m / 2.0), Constants.SHOTGUN_TURRET.OFFSET_Y.m + (Constants.SHOTGUN_TURRET.HEIGHT.m / 2.0)),
            Box2D.Common.Math.b2Vec2.Make(Constants.SHOTGUN_TURRET.OFFSET_X.m - (Constants.SHOTGUN_TURRET.WIDTH.m / 2.0), Constants.SHOTGUN_TURRET.OFFSET_Y.m + (Constants.SHOTGUN_TURRET.HEIGHT.m / 2.0)),
            Box2D.Common.Math.b2Vec2.Make(Constants.SHOTGUN_TURRET.OFFSET_X.m - (Constants.SHOTGUN_TURRET.WIDTH.m / 2.0), Constants.SHOTGUN_TURRET.OFFSET_Y.m - (Constants.SHOTGUN_TURRET.HEIGHT.m / 2.0)),
            Box2D.Common.Math.b2Vec2.Make(Constants.SHOTGUN_TURRET.OFFSET_X.m + (Constants.SHOTGUN_TURRET.WIDTH.m / 2.0), Constants.SHOTGUN_TURRET.OFFSET_Y.m - (Constants.SHOTGUN_TURRET.HEIGHT.m / 2.0))
        ];

        turretFixtureDef.shape = Box2D.Collision.Shapes.b2PolygonShape.AsArray(turretVertices);
        turretFixtureDef.userData = { type: "turret", gameObject: tank };
        turretFixtureDef.filter = new Box2D.Dynamics.b2FilterData();
        turretFixtureDef.filter.categoryBits = Constants.COLLISION_CATEGORIES.TANK;
        turretFixtureDef.filter.maskBits =
            Constants.COLLISION_CATEGORIES.TANK |
            Constants.COLLISION_CATEGORIES.MAZE |
            Constants.COLLISION_CATEGORIES.PROJECTILE |
            Constants.COLLISION_CATEGORIES.COLLECTIBLE |
            Constants.COLLISION_CATEGORIES.SHIELD |
            Constants.COLLISION_CATEGORIES.ZONE;

        return [turretFixtureDef];
    }

    static _createMissileTurretFixtureDef(tank) {
        const turretFixtureDef = new Box2D.Dynamics.b2FixtureDef();
        turretFixtureDef.density = 0.0;
        turretFixtureDef.friction = 0.25;
        turretFixtureDef.restitution = 0.0;
        const turretVertices = [
            Box2D.Common.Math.b2Vec2.Make(Constants.MISSILE_TURRET.OFFSET_X.m + (Constants.MISSILE_TURRET.WIDTH.m / 2.0), Constants.MISSILE_TURRET.OFFSET_Y.m + (Constants.MISSILE_TURRET.CENTRE_HEIGHT.m / 2.0)),
            Box2D.Common.Math.b2Vec2.Make(Constants.MISSILE_TURRET.OFFSET_X.m - (Constants.MISSILE_TURRET.WIDTH.m / 2.0), Constants.MISSILE_TURRET.OFFSET_Y.m + (Constants.MISSILE_TURRET.CENTRE_HEIGHT.m / 2.0)),
            Box2D.Common.Math.b2Vec2.Make(Constants.MISSILE_TURRET.OFFSET_X.m - (Constants.MISSILE_TURRET.WIDTH.m / 2.0), Constants.MISSILE_TURRET.OFFSET_Y.m - (Constants.MISSILE_TURRET.SIDE_HEIGHT.m / 2.0)),
            Box2D.Common.Math.b2Vec2.Make(Constants.MISSILE_TURRET.OFFSET_X.m, Constants.MISSILE_TURRET.OFFSET_Y.m - (Constants.MISSILE_TURRET.CENTRE_HEIGHT.m / 2.0)),
            Box2D.Common.Math.b2Vec2.Make(Constants.MISSILE_TURRET.OFFSET_X.m + (Constants.MISSILE_TURRET.WIDTH.m / 2.0), Constants.MISSILE_TURRET.OFFSET_Y.m - (Constants.MISSILE_TURRET.SIDE_HEIGHT.m / 2.0))
        ];

        turretFixtureDef.shape = Box2D.Collision.Shapes.b2PolygonShape.AsArray(turretVertices);
        turretFixtureDef.userData = { type: "turret", gameObject: tank };
        turretFixtureDef.filter = new Box2D.Dynamics.b2FilterData();
        turretFixtureDef.filter.categoryBits = Constants.COLLISION_CATEGORIES.TANK;
        turretFixtureDef.filter.maskBits =
            Constants.COLLISION_CATEGORIES.TANK |
            Constants.COLLISION_CATEGORIES.MAZE |
            Constants.COLLISION_CATEGORIES.PROJECTILE |
            Constants.COLLISION_CATEGORIES.COLLECTIBLE |
            Constants.COLLISION_CATEGORIES.SHIELD |
            Constants.COLLISION_CATEGORIES.ZONE;

        return [turretFixtureDef];
    }

    static _createGatlingGunTurretFixtureDefs(tank) {
        const turretFixtureDef = new Box2D.Dynamics.b2FixtureDef();
        turretFixtureDef.density = 0.0;
        turretFixtureDef.friction = 0.25;
        turretFixtureDef.restitution = 0.0;
        const turretVertices = [
            Box2D.Common.Math.b2Vec2.Make(Constants.GATLING_GUN_TURRET.OFFSET_X.m + (Constants.GATLING_GUN_TURRET.WIDTH.m / 2.0), Constants.GATLING_GUN_TURRET.OFFSET_Y.m + (Constants.GATLING_GUN_TURRET.HEIGHT.m / 2.0)),
            Box2D.Common.Math.b2Vec2.Make(Constants.GATLING_GUN_TURRET.OFFSET_X.m - (Constants.GATLING_GUN_TURRET.WIDTH.m / 2.0), Constants.GATLING_GUN_TURRET.OFFSET_Y.m + (Constants.GATLING_GUN_TURRET.HEIGHT.m / 2.0)),
            Box2D.Common.Math.b2Vec2.Make(Constants.GATLING_GUN_TURRET.OFFSET_X.m - (Constants.GATLING_GUN_TURRET.WIDTH.m / 2.0), Constants.GATLING_GUN_TURRET.OFFSET_Y.m - (Constants.GATLING_GUN_TURRET.HEIGHT.m / 2.0)),
            Box2D.Common.Math.b2Vec2.Make(Constants.GATLING_GUN_TURRET.OFFSET_X.m + (Constants.GATLING_GUN_TURRET.WIDTH.m / 2.0), Constants.GATLING_GUN_TURRET.OFFSET_Y.m - (Constants.GATLING_GUN_TURRET.HEIGHT.m / 2.0))
        ];

        turretFixtureDef.shape = Box2D.Collision.Shapes.b2PolygonShape.AsArray(turretVertices);
        turretFixtureDef.userData = { type: "turret", gameObject: tank };
        turretFixtureDef.filter = new Box2D.Dynamics.b2FilterData();
        turretFixtureDef.filter.categoryBits = Constants.COLLISION_CATEGORIES.TANK;
        turretFixtureDef.filter.maskBits =
            Constants.COLLISION_CATEGORIES.TANK |
            Constants.COLLISION_CATEGORIES.MAZE |
            Constants.COLLISION_CATEGORIES.PROJECTILE |
            Constants.COLLISION_CATEGORIES.COLLECTIBLE |
            Constants.COLLISION_CATEGORIES.SHIELD |
            Constants.COLLISION_CATEGORIES.ZONE;

        return [turretFixtureDef];
    }

    static createTankBody(b2dWorld, tank) {
        //enable physics on the player
        const tankBaseFixtureDef = new Box2D.Dynamics.b2FixtureDef();
        tankBaseFixtureDef.density = 1.0;
        tankBaseFixtureDef.friction = 0.25;
        tankBaseFixtureDef.restitution = 0.0;
        tankBaseFixtureDef.shape = Box2D.Collision.Shapes.b2PolygonShape.AsBox(
            Constants.TANK.WIDTH.m / 2.0,
            Constants.TANK.HEIGHT.m / 2.0
        );
        tankBaseFixtureDef.userData = { type: "base", gameObject: tank };
        tankBaseFixtureDef.filter = new Box2D.Dynamics.b2FilterData();
        tankBaseFixtureDef.filter.categoryBits = Constants.COLLISION_CATEGORIES.TANK;
        tankBaseFixtureDef.filter.maskBits =
            Constants.COLLISION_CATEGORIES.TANK |
            Constants.COLLISION_CATEGORIES.MAZE |
            Constants.COLLISION_CATEGORIES.PROJECTILE |
            Constants.COLLISION_CATEGORIES.TRAP |
            Constants.COLLISION_CATEGORIES.COLLECTIBLE |
            Constants.COLLISION_CATEGORIES.SHIELD |
            Constants.COLLISION_CATEGORIES.ZONE;

        const tankBodyDef = new Box2D.Dynamics.b2BodyDef();
        tankBodyDef.type = Box2D.Dynamics.b2Body.b2_dynamicBody;
        tankBodyDef.angle = tank.getRotation();
        tankBodyDef.linearDamping = 0.0;
        tankBodyDef.fixedRotation = false;
        tankBodyDef.active = true;
        tankBodyDef.allowSleep = false;
        const box2dBody = b2dWorld.CreateBody(tankBodyDef);
        box2dBody.CreateFixture(tankBaseFixtureDef);
        box2dBody.CreateFixture(B2DUtils._createBulletTurretFixtureDefs(tank)[0]);
        box2dBody.SetPosition(
            Box2D.Common.Math.b2Vec2.Make(tank.getX(), tank.getY())
        );

        return box2dBody;
    }

    static getWorldManifold() {
        if (B2DUtils._manifoldInstance === null) {
            B2DUtils._manifoldInstance = new Box2D.Collision.b2WorldManifold();
        }

        return B2DUtils._manifoldInstance;
    }

    static getContactData(b2dContact) {
        const categoryBitsA = b2dContact.GetFixtureA().GetFilterData().categoryBits;
        const categoryBitsB = b2dContact.GetFixtureB().GetFilterData().categoryBits;
        const fixtureA = b2dContact.GetFixtureA();
        const fixtureB = b2dContact.GetFixtureB();

        const data = {};
        data.contactBits = categoryBitsA | categoryBitsB;

        const worldManifold = B2DUtils.getWorldManifold();
        b2dContact.GetWorldManifold(worldManifold);

        // Make a copy of the collision point since it will be overwritten by other collisions.
        data.collisionPoint = worldManifold.m_points[0].Copy();
        data.collisionNormal = worldManifold.m_normal.Copy();

        switch (categoryBitsA) {
            case Constants.COLLISION_CATEGORIES.TANK:
            {
                data.tankA = fixtureA.GetUserData().gameObject;
                break;
            }
            case Constants.COLLISION_CATEGORIES.MAZE:
            {
                data.maze = fixtureA.GetUserData().gameObject;
                break;
            }
            case Constants.COLLISION_CATEGORIES.PROJECTILE:
            {
                data.projectile = fixtureA.GetUserData().gameObject;
                break;
            }
            case Constants.COLLISION_CATEGORIES.TRAP:
            {
                data.trap = fixtureA.GetUserData().gameObject;
                break;
            }
            case Constants.COLLISION_CATEGORIES.COLLECTIBLE:
            {
                data.collectible = fixtureA.GetUserData().gameObject;
                break;
            }
            case Constants.COLLISION_CATEGORIES.SHIELD:
            {
                data.shieldA = fixtureA.GetUserData().gameObject;
                break;
            }
            case Constants.COLLISION_CATEGORIES.ZONE:
            {
                data.zone = fixtureA.GetUserData().gameObject;
                break;
            }
            default:
            {
                console.log("Unknown collision category in B2DUtils.getContactData");
            }
        }

        switch (categoryBitsB) {
            case Constants.COLLISION_CATEGORIES.TANK:
            {
                if (data.tankA) {
                    data.tankB = fixtureB.GetUserData().gameObject;
                } else {
                    data.tankA = fixtureB.GetUserData().gameObject;
                }

                break;
            }
            case Constants.COLLISION_CATEGORIES.MAZE:
            {
                data.maze = fixtureB.GetUserData().gameObject;
                break;
            }
            case Constants.COLLISION_CATEGORIES.PROJECTILE:
            {
                data.projectile = fixtureB.GetUserData().gameObject;
                break;
            }
            case Constants.COLLISION_CATEGORIES.TRAP:
            {
                data.trap = fixtureB.GetUserData().gameObject;
                break;
            }
            case Constants.COLLISION_CATEGORIES.COLLECTIBLE:
            {
                data.collectible = fixtureB.GetUserData().gameObject;
                break;
            }
            case Constants.COLLISION_CATEGORIES.SHIELD:
            {
                if (data.shieldA) {
                    data.shieldB = fixtureB.GetUserData().gameObject;
                } else {
                    data.shieldA = fixtureB.GetUserData().gameObject;
                }

                break;
            }
            case Constants.COLLISION_CATEGORIES.ZONE:
            {
                data.zone = fixtureB.GetUserData().gameObject;
                break;
            }
            default:
            {
                console.log("Unknown collision category in B2DUtils.getContactData");
            }
        }

        return data;
    }

    static calculateProjectilePath(b2dWorld, projectile, maxBounces, maxLength, collideWithTanks) {
        const b2body = projectile.getB2DBody();
        return B2DUtils.calculatePath(b2dWorld, b2body.GetPosition(), b2body.GetLinearVelocity(), maxBounces, maxLength, collideWithTanks);
    }

    static calculateFiringPath(b2dWorld, tank, angle, maxBounces, maxLength, collideWithTanks) {
        const b2body = tank.getB2DBody();
        const direction = Box2D.Common.Math.b2Math.MulMV(
            Box2D.Common.Math.b2Mat22.FromAngle(b2body.GetAngle() + angle),
            Box2D.Common.Math.b2Vec2.Make(0, -Constants.TANK.HEIGHT.m * 0.5)
        );
        const position = b2body.GetPosition().Copy();
        position.x += direction.x;
        position.y += direction.y;
        return B2DUtils.calculatePath(b2dWorld, position, direction, maxBounces, maxLength, collideWithTanks);
    }

    static calculatePath(b2dWorld, position, direction, maxBounces, maxLength, collideWithTanks) {
        const path = [];
        let hit = null;
        let pathLength = 0;
        let firstSegmentLength = 0;
        path.push(position);
        let remainingLength = maxLength;
        let bounces = 0;
        // Initialise ray.
        const currentDirection = direction.Copy();
        currentDirection.Normalize();
        const currentPosition = position.Copy();
        const nextPosition = currentPosition.Copy();
        nextPosition.x += currentDirection.x * remainingLength;
        nextPosition.y += currentDirection.y * remainingLength;
        // Intersection values.
        let closestFixture = null;
        const closestPoint = Box2D.Common.Math.b2Vec2.Make(0, 0);
        const closestNormal = Box2D.Common.Math.b2Vec2.Make(0, 0);
        let closestFraction = 1;
        while (remainingLength > 0 && bounces <= maxBounces) {
            b2dWorld.RayCast((fixture, point, normal, fraction) => {
                // Ignore all projectiles and collectibles!
                if (fixture.GetFilterData().categoryBits === Constants.COLLISION_CATEGORIES.PROJECTILE ||
                    fixture.GetFilterData().categoryBits === Constants.COLLISION_CATEGORIES.COLLECTIBLE) {
                    return closestFraction;
                }
                // Ignore tanks if asked to!
                if (!collideWithTanks && fixture.GetFilterData().categoryBits === Constants.COLLISION_CATEGORIES.TANK) {
                    return closestFraction;
                }
                // If closer than previous intersection, update intersection values.
                // This should not be necessary according to documentation, but it seems to be.
                if (fraction < closestFraction) {
                    closestFixture = fixture;
                    closestPoint.SetV(point);
                    closestNormal.SetV(normal);
                    closestFraction = fraction;
                }
                return closestFraction;
            }, currentPosition, nextPosition);
            if (closestFixture) {
                // Put a copy into path to be able to reuse closestPoint.
                path.push(closestPoint.Copy());
                if (closestFixture.GetFilterData().categoryBits === Constants.COLLISION_CATEGORIES.MAZE ||
                    closestFixture.GetFilterData().categoryBits === Constants.COLLISION_CATEGORIES.SHIELD) {
                    // Continue ray.
                    const rayLength = Math.max(Constants.PATH_MIN_STEP_LENGTH, closestFraction * remainingLength);
                    pathLength += rayLength;
                    remainingLength -= rayLength;
                    // Reflect direction around normal.
                    closestNormal.Multiply(-2 * Box2D.Common.Math.b2Math.Dot(currentDirection, closestNormal));
                    currentDirection.x += closestNormal.x;
                    currentDirection.y += closestNormal.y;
                    // Update current and next position.
                    currentPosition.SetV(closestPoint);
                    nextPosition.SetV(currentPosition);
                    nextPosition.x += currentDirection.x * remainingLength;
                    nextPosition.y += currentDirection.y * remainingLength;
                } else {
                    // End path here.
                    pathLength += closestFraction * remainingLength;
                    remainingLength = 0;
                    // Store hit if tank was closest.
                    if (closestFixture.GetFilterData().categoryBits === Constants.COLLISION_CATEGORIES.TANK) {
                        hit = closestFixture.GetUserData().gameObject;
                    }
                }
            } else {
                // No intersection. End path at next position.
                pathLength += remainingLength;
                remainingLength = 0;
                path.push(nextPosition);
            }
            // Store first segment length.
            if (bounces === 0) {
                firstSegmentLength = pathLength;
            }
            // Reset intersection values for next ray.
            closestFraction = 1;
            closestFixture = null;
            // Increase bounces.
            ++bounces;
        }

        return { path, hit, firstSegmentLength, length: pathLength, direction };
    }

    static splatPathUntoMazeMap(map, path, stepSize, splatFunction) {
        const mapData = map.data();

        // First validate path.
        for (const position of path) {
            const tile = {
                x: Math.floor(position.x / Constants.MAZE_TILE_SIZE.m),
                y: Math.floor(position.x / Constants.MAZE_TILE_SIZE.m)
            };
            if (!map.isPositionInsideMap(tile)) {
                return;
            }
        }

        // Then do the splatting.
        let totalLength = 0;
        let segmentSample = 0.0;
        const tile = Box2D.Common.Math.b2Vec2.Make(0, 0);
        for (let i = 1; i < path.length; ++i) {
            const segmentStart = path[i - 1];
            const segmentEnd = path[i];
            const segmentDirection = segmentEnd.Copy();
            segmentDirection.Subtract(segmentStart);
            const segmentLength = segmentDirection.Normalize();

            while (segmentSample < segmentLength) {
                const current = segmentDirection.Copy();
                current.Multiply(segmentSample);
                current.Add(segmentStart);

                tile.Set(Math.floor(current.x / Constants.MAZE_TILE_SIZE.m), Math.floor(current.y / Constants.MAZE_TILE_SIZE.m));

                mapData[tile.x][tile.y] += splatFunction(tile, totalLength + segmentSample, stepSize);

                segmentSample += stepSize;
            }

            totalLength += segmentLength;
            segmentSample -= segmentLength;
        }
    }

    static checkLineForMazeCollision(b2dWorld, startPosition, endPosition) {
        let hitMaze = false;
        b2dWorld.RayCast((fixture, point, normal, fraction) => {
            // Ignore everything except maze!
            if (fixture.GetFilterData().categoryBits !== Constants.COLLISION_CATEGORIES.MAZE) {
                return 1;
            }

            hitMaze = true;
            return 0;
        }, startPosition, endPosition);

        return hitMaze;
    }

    static checkTankStateForMazeCollision(b2dWorld, b2body, tankState) {
        const tankStatePosition = Box2D.Common.Math.b2Vec2.Make(tankState.getX(), tankState.getY());
        const tankStateRotation = Box2D.Common.Math.b2Mat22.FromAngle(tankState.getRotation());
        const tankStateTransform = new Box2D.Common.Math.b2Transform(tankStatePosition, tankStateRotation);

        let hitMaze = false;
        let currentFixture = b2body.GetFixtureList();
        while (!hitMaze && currentFixture !== null) {
            // Ignore everything except base and turret!
            if (currentFixture.GetUserData().type === "base" || currentFixture.GetUserData().type === "turret") {
                b2dWorld.QueryShape(() => {
                    hitMaze = true;
                    return false;
                }, currentFixture.GetShape(), tankStateTransform);
            }

            currentFixture = currentFixture.GetNext();
        }

        return hitMaze;
    }

    static toLocalSpace(b2body, position) {
        const worldPoint = Box2D.Common.Math.b2Vec2.Make(position.x, position.y);
        const localPoint = b2body.GetLocalPoint(worldPoint);
        return localPoint;
    }

    static directionToLocalSpace(b2body, direction) {
        const worldPoint = b2body.GetPosition().Copy();
        worldPoint.Add(direction);
        const localPoint = b2body.GetLocalPoint(worldPoint);
        return localPoint;
    }
}
